import {useState, useMemo} from 'react';
import {FileInput, Button, TextInput} from '@burna/monster-design-system';
import {useHistory} from 'react-router-dom';
import {useDropzone} from 'react-dropzone';
import axios from 'axios';
import Stats from 'components/stats';
import useUser from 'hooks/useUser';
import useAlertWithTimeout from 'hooks/useAlertWithTimeout';
import {getFileSizeString} from 'utils';
import {APIName} from 'config';
import styles from './index.module.scss';

const imageFileExtensions = ['jpg', 'jpeg', 'png', 'bmp', 'tiff', 'ico'];
const videoFileExtensions = ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm'];
function getFileIcon(fileName) {
  const extension = fileName.split('.').pop();
  if (imageFileExtensions.includes(extension)) {
    return 'image';
  }
  if (videoFileExtensions.includes(extension)) {
    return 'film';
  }
  return 'file';
}

function Uploader() {
  const history = useHistory();
  const user = useUser();
  const alertWithTimeout = useAlertWithTimeout();
  const [remoteUrl, setRemoteUrl] = useState('');
  const [file, setFile] = useState(null);
  const [controller, setController] = useState(() => new AbortController());

  const redirect = managePath => history.push(user.username ? '/fileList' : `/manage/${managePath}`);

  const uploadFile = () => {
    setFile(({...file, status: 'in-progress'}));
    const formData = new FormData();
    formData.append('binary', file.fileObject);
    axios
      .post(`/api/${APIName}/uploadFile`, formData, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: progressEvent => {
          setFile(file => ({
            ...file,
            percent: (100 * progressEvent.loaded) / progressEvent.total,
          }));
        },
      })
      .then(async ({data}) => {
        setFile(file => ({
          ...file,
          removable: false,
        }));
        await axios.post(`/api/${APIName}/processFile`, {
          filePath: data.filePath,
          fileName: data.fileName,
        }).then(({data}) => {
          redirect(data.managePath);
        });
      })
      .catch((error) => {
        console.log(error);
        setFile(file => ({
          ...file,
          percent: null,
          removable: true,
          status: 'error',
          errorText: error?.name === 'CanceledError' ? 'upload canceled' : undefined,
        }));
      })
      .then(() => {
        setController(new AbortController());
      });
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (file) {
      uploadFile();
      return;
    }
    axios
      .post(`/api/${APIName}/remote`, {
        url: remoteUrl,
        signal: controller.signal,
        onUploadProgress: progressEvent => {
          setFile(file => ({
            ...file,
            percent: (100 * progressEvent.loaded) / progressEvent.total,
          }));
        },
      },)
      .then(({data}) => {
        return axios.post(`/api/${APIName}/processFile`, {
          sourceUser: user.username,
          filePath: data.filePath,
          fileName: data.fileName,
        }).then(({data}) => {
          redirect(data.managePath);
        });
        // .catch(() => {
        //   // TODO: setInvalidUser
        // });
      })
      .catch((error) => {
        error.response?.status === 413
          ? alertWithTimeout('File is too large', `Maximum size allowed: ${maxSizeString}.`, 3000, 'error')
          : error.response?.status === 403
            ? alertWithTimeout('Access Denied', 'The provided URL is forbidden.', 3000, 'error')
            : [301, 302, 303, 307, 308].includes(error.response?.status)
              ? alertWithTimeout('Redirect Detected', 'Redirects are not allowed.', 3000, 'error')
              : error.response?.status
                ? alertWithTimeout('Server Error!', 'Server Error!', 3000, 'error')
                : alertWithTimeout('Failed to upload', 'Please try again.', 3000, 'error');
      })
      .then(() => {
        setController(new AbortController());
      });
  };

  const removeFile = ()=>{
    if(!uploading) {
      setFile(null);
    } else if(file.percent !== null) {
      controller.abort('The user has canceled the file upload.');
    }
  };

  const maxSize = useMemo(() => (user.username ? MAX_FILE_SIZE : 20) * 1024 * 1024, [user.username]);
  const maxSizeString = useMemo(() => getFileSizeString(maxSize, 1), [maxSize]);

  const handleDrop = acceptedFiles => {
    const fileObject = acceptedFiles[0];
    const {name, size} = fileObject;
    if (size === undefined) {
      alertWithTimeout('Error opening file', 'An unkown error occurred while trying to open the file. Please try again.', 3000, 'error');
      return;
    }
    if (size > maxSize) {
      alertWithTimeout('File is too large', `The  file shouldn't be bigger than ${maxSizeString}`, 3000, 'error');
      return;
    }
    setFile({
      fileObject,
      name,
      icon: getFileIcon(name),
      size: getFileSizeString(size),
      status: 'in-progress',
      percent: null,
      removable: true,
    });
  };

  const {getRootProps, getInputProps, isDragActive} = useDropzone({
    multiple: false,
    onDrop: handleDrop,
  });

  const uploading = file && file.percent !== null;

  return (
    <section className={styles.container}>
      <div className={styles.wrapper}>
        <form
          onSubmit={handleSubmit}
          className={styles.form}
          disabled={uploading}
        >
          <p className={styles.tagline}>Upload and Access</p>
          <p className={styles.tagline2}>your files securely.<div className={styles.arrow} /></p>
          <FileInput
            {...getRootProps()}
            max={maxSizeString}
            finishedUploading={file?.percent === 100}
            files={file ? [file] : []}
            onRemove={removeFile}
            onRetry={() => uploadFile()}
            extraBorder
          />
          <input {...getInputProps()} disabled={uploading} />
          {!file && <>
            <p className={styles.urlCtaText}>OR enter the file URL below:</p>
            <TextInput
              type="url"
              placeholder="Enter URL"
              value={remoteUrl}
              onChange={e => setRemoteUrl(e.target.value)}
              disabled={uploading}
            />
          </>}
          <Button
            className={styles.submit}
            type="submit"
            con="upload"
            text="Upload"
            disabled={uploading || (!remoteUrl && !file)}
          />
        </form>
        <div className={styles.graph} />
      </div>
      <Stats />
    </section>
  );
}

export default Uploader;
